// Cliente 360 (Ola 3, chunk 3) — Celda del grid Info: KPIs operativos
// (deuda vencida, factura del período, último pago) + fila ancho completo con
// el ciclo de facturación, reusando el patrón MetricCard de chunk 2.
// Errores degradan a '—': el dashboard nunca cae.
// Nota: la deuda total (todas las facturas abiertas) vive en ClienteKPIs
// (fila superior del 360); esta tarjeta muestra solo el subconjunto vencido.
import { Col, Row } from 'antd';
import { AccountBookOutlined, FieldTimeOutlined, WalletOutlined } from '@ant-design/icons';
import { useClienteResumen, useClienteCobros } from '../api/useClientes';
import { usePagosList } from '../../pagos/api/usePagos';
import {
  calcularDiasMora,
  diasDesdeUltimoPago,
  facturaMoraVencidaMasAntigua,
  facturaPeriodoMasAntigua,
  facturaVigenteMasAntigua,
  montoMoraVencida,
  nFacturasVencidas,
  pendienteMesActual,
  ultimoPagoConValor,
} from '../cliente-financiero';
import { ClienteCicloFacturacionCard } from './ClienteCicloFacturacionCard';
import { StatusTag } from '../../../components/StatusTag';
import { MetricCard } from '../../../components/MetricCard';
import { formatCurrency } from '../../../lib/format';
import { useT } from '../../../locales';
import dayjs from 'dayjs';
import { fechaVencimientoFactura } from '@aid/shared';
import type { ClienteResponse } from '@aid/shared';

const DATE_FORMAT = 'DD/MM/YYYY';
const DIAS_MORA_WARNING = 90;

interface ClienteKPIsFinancierosProps {
  cliente: ClienteResponse;
}

export function ClienteKPIsFinancieros({ cliente }: ClienteKPIsFinancierosProps) {
  const t = useT();

  const resumen = useClienteResumen(cliente.id);
  const cobrosQuery = useClienteCobros(cliente.id, { limit: 100, offset: 0 });
  // El backend limita limit a 100 (zod max(100) en aid-shared); el volumen real
  // de pagos por cliente entra sin paginar (mismo patrón que ClienteSerieMensualChart).
  const pagosQuery = usePagosList({ idCliente: cliente.id, limit: 100, offset: 0 });

  const cobros = cobrosQuery.data?.data ?? [];
  const pagos = pagosQuery.data?.data ?? [];
  const isLoading = resumen.isLoading || cobrosQuery.isLoading || pagosQuery.isLoading;
  const isError = resumen.isError || cobrosQuery.isError || pagosQuery.isError;

  const facturaAntigua = facturaVigenteMasAntigua(cobros);
  const facturaMoraAntigua = facturaMoraVencidaMasAntigua(cobros);
  // billing-domain-rules: the day-overdue rule consumes the POSITIVE
  // deudaPendiente contract (text NUMERIC from view_client_balance). The
  // legacy signed `saldo` alias must not feed this helper — passing it made
  // every indebted client render as "Al día" (regression fixed here).
  const deudaPendienteNum = resumen.data
    ? Number.parseFloat(resumen.data.deudaPendiente)
    : undefined;
  const diasMora = calcularDiasMora(
    deudaPendienteNum !== undefined && Number.isFinite(deudaPendienteNum)
      ? deudaPendienteNum
      : undefined,
    facturaMoraAntigua ?? facturaAntigua,
    cliente,
  );

  const montoVencido = montoMoraVencida(cobros);
  const nVencidas = nFacturasVencidas(cobros);
  const pendienteMes = pendienteMesActual(cobros);
  const facturaPeriodoAntigua = facturaPeriodoMasAntigua(cobros);
  const ultimoPago = ultimoPagoConValor(pagos);

  // Tarjeta 1 — Deuda vencida: el monto es el protagonista; el badge es señal
  // secundaria. "Al día" SOLO aparece cuando no hay facturas vencidas; con
  // mora real se muestra el contador de días (nunca "Al día").
  const moraValue = isError ? (
    '—'
  ) : (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(montoVencido)}</span>
      {montoVencido <= 0 ? (
        <StatusTag tone="success" style={{ fontSize: 12, fontWeight: 600 }}>
          {t('pages.clientes.kpi.alDia')}
        </StatusTag>
      ) : diasMora !== null && diasMora > 0 ? (
        <StatusTag
          tone={diasMora <= DIAS_MORA_WARNING ? 'warning' : 'error'}
          style={{ fontSize: 12, fontWeight: 600 }}
        >
          {t('pages.clientes.kpi.moraDias', { dias: diasMora })}
        </StatusTag>
      ) : null}
    </span>
  );

  // Caption de alcance: distingue esta métrica (subconjunto vencido) de la
  // deuda total del header, que además incluye el período en curso.
  const moraCaption = isError
    ? undefined
    : nVencidas === 0
      ? t('pages.clientes.kpi.montoMoraSinVencidas')
      : t('pages.clientes.kpi.moraVencidaFacturas', {
          n: nVencidas,
          fecha: dayjs(facturaMoraAntigua!.fechaCobro).format(DATE_FORMAT),
        });

  const facturaPeriodoValue = isError ? (
    '—'
  ) : (
    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(pendienteMes.monto)}</span>
  );

  // El vencimiento (emisión + 15 días de gracia, regla de @aid/shared) es el
  // caption principal; la mora real la comunica el badge de la tarjeta 1.
  const vencimientoPeriodo = facturaPeriodoAntigua
    ? fechaVencimientoFactura(facturaPeriodoAntigua.fechaCobro)
    : null;
  const facturaPeriodoCaption = isError
    ? undefined
    : pendienteMes.n > 0
      ? vencimientoPeriodo
        ? t('pages.clientes.kpi.facturaPeriodoVence', {
            fecha: dayjs(vencimientoPeriodo).format(DATE_FORMAT),
          })
        : t(
            pendienteMes.n === 1
              ? 'pages.clientes.kpi.facturaPeriodoOne'
              : 'pages.clientes.kpi.facturaPeriodoOther',
            { n: pendienteMes.n },
          )
      : t('pages.clientes.kpi.facturaPeriodoSinFacturas');

  const diasUltimoPago = ultimoPago ? diasDesdeUltimoPago(ultimoPago.fechaPago) : null;

  const ultimoPagoValue =
    isError || !ultimoPago ? (
      '—'
    ) : (
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>
        {dayjs(ultimoPago.fechaPago).format(DATE_FORMAT)}
      </span>
    );

  const ultimoPagoCaption =
    isError || diasUltimoPago === null
      ? undefined
      : diasUltimoPago === 0
        ? t('pages.clientes.kpi.ultimoPagoHoy')
        : diasUltimoPago === 1
          ? t('pages.clientes.kpi.ultimoPagoAyer')
          : t('pages.clientes.kpi.ultimoPagoHace', { dias: diasUltimoPago });

  return (
    <Row gutter={[12, 12]} style={{ flex: 1, minWidth: 0 }}>
      <Col xs={24} lg={8}>
        <MetricCard
          valueSize="md"
          label={t('pages.clientes.kpi.montoMora')}
          loading={isLoading}
          icon={<WalletOutlined />}
          value={moraValue}
          caption={moraCaption}
        />
      </Col>
      <Col xs={24} lg={8}>
        <MetricCard
          valueSize="md"
          label={t('pages.clientes.kpi.facturaPeriodo')}
          loading={isLoading}
          icon={<AccountBookOutlined />}
          value={facturaPeriodoValue}
          caption={facturaPeriodoCaption}
        />
      </Col>
      <Col xs={24} lg={8}>
        <MetricCard
          valueSize="md"
          label={t('pages.clientes.kpi.ultimoPago')}
          loading={isLoading}
          icon={<FieldTimeOutlined />}
          value={ultimoPagoValue}
          caption={ultimoPagoCaption}
        />
      </Col>
      <Col xs={24}>
        <ClienteCicloFacturacionCard
          cliente={cliente}
          cobros={cobros}
          loading={isLoading}
          isError={isError}
        />
      </Col>
    </Row>
  );
}
